import { useMsal } from '@azure/msal-react'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Anchor,
  Group,
  Skeleton,
  Stack,
  Title,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import CartSummary from '../components/checkout/CartSummary'
import StripeCheckoutWrapper from '../components/checkout/StripeCheckoutWrapper'
import { useSessionInfo } from '../contexts/SessionContext'
import {
  createPaymentIntent,
  fetchAdvertisementsInCart,
  fetchAllLocationsBundlesInCart,
} from '../data/checkout.service'
import { AllLocationsBundle } from '../models/Checkout/AllLocationsBundle'
import { CartDeviceAdvertisement } from '../models/Checkout/CartDeviceAdvertisement'
import { ServerPaymentIntent } from '../models/Checkout/ServerPaymentIntent'
import { applyCouponDiscount, calculateTotals } from '../utils/checkoutUtils'

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '',
)

function CheckoutPage() {
  const { session, setSession } = useSessionInfo()
  const [cartItems, setCartItems] = useState<CartDeviceAdvertisement[]>([])
  const [bundleItems, setBundleItems] = useState<AllLocationsBundle[]>([])
  const [paymentIntent, setPaymentIntent] = useState<ServerPaymentIntent>()
  const [isLoading, setIsLoading] = useState(true)
  const [isPlacingFreeOrder, setIsPlacingFreeOrder] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const couponCode = (location.state as { couponCode?: string } | null)?.couponCode
  const { instance } = useMsal()
  const isMobile = useMediaQuery('(max-width: 768px)')

  useEffect(() => {
    const fetchData = async () => {
      if (!session?.organizationId) {
        setIsLoading(false)
        return
      }

      try {
        const [cartResponse, bundlesResponse, paymentIntentResponse] = await Promise.all([
          fetchAdvertisementsInCart(session.organizationId, instance),
          fetchAllLocationsBundlesInCart(session.organizationId, instance),
          createPaymentIntent(
            session.organizationId,
            session.user.id,
            instance,
            couponCode,
          ),
        ])

        const itemsWithDates = cartResponse.map((item) => ({
          ...item,
          startDateLocal: item.startDateLocal
            ? new Date(item.startDateLocal)
            : null,
          endDateLocal: item.endDateLocal ? new Date(item.endDateLocal) : null,
        }))

        setCartItems(itemsWithDates)
        setBundleItems(bundlesResponse)
        setPaymentIntent(paymentIntentResponse)

        if (paymentIntentResponse.autoRenewWasDisabled) {
          notifications.show({
            title: 'Auto-Renew Disabled',
            message:
              'Auto-renew has been turned off for your free-month ads. Free orders cannot be renewed automatically — you will need to manually check out again when your campaign ends.',
            color: 'yellow',
            autoClose: false,
          })
        }
      } catch (error: any) {
        console.error('Error fetching data:', error)
        if (error.response?.status === 400) {
          notifications.show({
            title: 'Checkout Error',
            message:
              typeof error.response?.data === 'string' && error.response.data
                ? error.response.data
                : 'Unable to proceed to checkout. Please review your cart and try again.',
            autoClose: false,
          })
          navigate('/cart')
        } else {
          notifications.show({
            title: 'Error initializing payment',
            message:
              'An unknown error occurred while initializing the payment. Please refresh the page.',
            autoClose: false,
          })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [session?.organizationId, session?.user.id, instance, navigate, couponCode])

  const handlePlaceFreeOrder = async () => {
    setIsPlacingFreeOrder(true)
    if (session) {
      setSession({ ...session, itemsInCart: [], allLocationsBundleCount: 0 })
    }
    navigate('/dashboard/myAds')
    notifications.show({
      title: 'Order placed!',
      message: 'Your ads will be up and running shortly',
      color: 'green',
    })
  }

  const totals = calculateTotals(cartItems)
  const bundleTotal = bundleItems.reduce((sum, bundle) => sum + bundle.price, 0)
  const hasCheckoutItems = cartItems.length > 0 || bundleItems.length > 0

  const { discountedTotals, couponDiscount, discountedBundleTotal } = applyCouponDiscount(
    totals,
    cartItems,
    bundleTotal,
    !!couponCode,
  )

  const backToCartLink = (
    <Stack gap={8}>
      <Anchor
        component={Link}
        to='/cart'
        size='sm'
        style={{ width: 'fit-content', display: 'inline-block' }}
      >
        <Group gap={8} wrap='nowrap'>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Cart
        </Group>
      </Anchor>
      <Title size='h2'>Billing Details</Title>
    </Stack>
  )

  if (!paymentIntent || isLoading) {
    return (
      <Stack
        p={isMobile ? 20 : 50}
        h='100%'
        gap={isMobile ? 20 : 30}
        w='100%'
        justify='flex-start'
      >
        {backToCartLink}
        {isMobile ? (
          <Stack gap={20}>
            <Skeleton height={200} width='100%' radius={20} />
            <Skeleton height={400} width='100%' radius={20} />
          </Stack>
        ) : (
          <Group gap={50} h='100%' w='100%' align='flex-start'>
            <Skeleton flex={8} radius={20} h='60%' />
            <Skeleton flex={2} radius={20} h='50%' />
          </Group>
        )}
      </Stack>
    )
  }

  return (
    <Stack
      p={isMobile ? 20 : 50}
      h='100%'
      gap={isMobile ? 20 : 30}
      w='100%'
      justify='flex-start'
    >
      {backToCartLink}

      {paymentIntent.isFree ? (
        <CartSummary
          totals={{ totalAmount: 0, totalDueNow: 0, totalDueAfterApproval: 0 }}
          onSubmit={handlePlaceFreeOrder}
          isButtonDisabled={isPlacingFreeOrder}
          buttonText='Place Order (Free)'
          isLoadingSubmit={isPlacingFreeOrder}
          buttonDisabledText=''
        />
      ) : (
        <StripeCheckoutWrapper
          stripePromise={stripePromise}
          clientSecret={paymentIntent.clientSecret ?? ''}
          totals={discountedTotals}
          discount={couponDiscount || undefined}
          bundleTotal={discountedBundleTotal || undefined}
          hasCheckoutItems={hasCheckoutItems}
        />
      )}
    </Stack>
  )
}

export default CheckoutPage
